import React, { useEffect, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import { UseOutletContextTreasury } from './Treasury';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { TreasuryAccountDA } from '../../../services/admin/treasury.account.service';
import { Error } from '../../../models/error.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Message } from '../../../models/message.model';
import { TreasuryAccount } from '../../../models/treasury.account.model';
import { uploadTooltip } from '../../../utils/tooltip.util';
import { modalShow } from '../../../utils/modal.util';
import { formatAccountingAccount, formatBIC, formatIBAN } from '../../../scripts/format.value.script';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { countryName } from '../../../libraries/country.library';
import { treasuryAccountTypeValue } from '../../../libraries/treasury.account.type.library';
import { getError, getMessage } from '../../../languages/translations/response';

import AppPreloader from '../../../components/Preloader';
import AppModalTreasuryUpdate from '../../../components/modal/ModalTreasuryAccountUpdate';
import background from '../../../assets/themes/images/question.svg';
import EmptyState from '../../../components/element/EmptyState';

export interface TreasuryListPageProps {};

let errorResponse: Error, messageResponse: Message;

const TreasuryListPage: React.FunctionComponent<TreasuryListPageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany, loadAccountingAvailableAccountsForCompany, loadTreasuryAccountsForCompany} = UseOutletContextTreasury()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [treasuryAccount, setTreasuryAccount] = useState<TreasuryAccount | null>(null)

  const submitTreasuryAccountUpdateDefault = async (item: TreasuryAccount) => {
    if (companyForUser && !item.default) {
      GlobalSwal.fire({
        title: lang.labels.youWantToSelectThisTreasuryAccountAsPrimary,
        text: lang.labels.youWillBeAbleToUndoThisAction,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesSelect,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await TreasuryAccountDA.putTreasuryAccountDefault(companyForUser.id, item.id).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data
              loadTreasuryAccountsForCompany(companyForUser.id)

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitTreasuryAccountDelete = async (item: TreasuryAccount) => {
    if (companyForUser) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisTreasuryAccount,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await TreasuryAccountDA.deleteTreasuryAccount(companyForUser.id, item.id).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data
              loadTreasuryAccountsForCompany(companyForUser.id)
              loadAccountingAvailableAccountsForCompany(companyForUser.id)

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const executeShowModalTreasuryAccountUpdate = (item: TreasuryAccount) => {
    setTreasuryAccount(item)
    modalShow('modal-treasury-account-update')
  }

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ treasuryAccountsForCompany])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.treasuryAccounts, branch: lang.labels.listTreasuryAccounts}, company: true})

    if (companyForUser) {
      loadTreasuryAccountsForCompany(companyForUser.id)
    }

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && treasuryAccountsForCompany
        ?
        <>
        { treasuryAccountsForCompany.length > 0
          ?
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-2">
            { treasuryAccountsForCompany.map (( (item, index) => { return (
              <div key={index} className="col">
                <div className="card card-flush h-md-100">
                  <div className="card-header flex-nowrap">
                    <div className="card-title">
                      <h4>{item.name}</h4>
                    </div>
                    <div className="card-toolbar">
                      <button className="btn btn-sm btn-icon btn-flex btn-bg-light btn-hover-color-info h-30px w-30px" type="button" onClick={() => submitTreasuryAccountUpdateDefault(item)}>
                        <span className={`svg-icon svg-icon-3 ${item.default && "svg-icon-info"}`}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.0079 2.6L15.7079 7.2L21.0079 8.4C21.9079 8.6 22.3079 9.7 21.7079 10.4L18.1079 14.4L18.6079 19.8C18.7079 20.7 17.7079 21.4 16.9079 21L12.0079 18.8L7.10785 21C6.20785 21.4 5.30786 20.7 5.40786 19.8L5.90786 14.4L2.30785 10.4C1.70785 9.7 2.00786 8.6 3.00786 8.4L8.30785 7.2L11.0079 2.6C11.3079 1.8 12.5079 1.8 13.0079 2.6Z" fill="black"/>
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body py-0">
                    <div className="d-flex flex-column">
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8">{lang.labels.typeOfTreasuryAccount}</label>
                        <label className="fw-bolder text-uppercase">{treasuryAccountTypeValue(lang, item.type)}</label>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8">{lang.labels.accountingAccount}</label>
                        <label className="fw-bolder text-uppercase text-nowrap d-flex align-items-center">
                          {formatAccountingAccount(item.accounting_account, companyForUser.language)}
                          <span className="fs-7 ms-1">{"["}{item.accounting_account?.currency?.symbol ?? ''}{"]"}</span>
                        </label>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8 d-flex align-items-center">
                          {lang.labels.iban}
                          <i className="ms-1 fs-10" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumber}><FaExclamationCircle /></i>
                        </label>
                        <label className="fw-bolder text-uppercase">{formatIBAN(lang, item.iban)}</label>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8 d-flex align-items-center">
                          {lang.labels.bban}
                          <i className="ms-1 fs-10" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicBankAccountNumber}><FaExclamationCircle /></i>
                        </label>
                        <label className="fw-bolder text-uppercase">{item.bban.length > 0 ? item.bban : lang.labels.none}</label>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8 d-flex align-items-center">
                          {lang.labels.bicSwift}
                          <i className="ms-1 fs-10" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.bankIdentifierCode}><FaExclamationCircle /></i>
                        </label>
                        <label className="fw-bolder text-uppercase">{formatBIC(lang, item.bic)}</label>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8 d-flex align-items-center">
                          {lang.labels.membership}
                          <i className="ms-1 fs-10" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.codeOfBelongingToABankingOrganization}><FaExclamationCircle /></i>
                        </label>
                        <label className="fw-bolder text-uppercase">{item.membership.length > 0 ? item.membership : lang.labels.none}</label>
                      </div>
                      <div className="row mb-2">
                        <label className="text-muted fw-bold fs-8 d-flex align-items-center">
                          {lang.labels.qrIban}
                          <i className="ms-1 fs-10" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumberIban}><FaExclamationCircle /></i>
                        </label>
                        <label className="fw-bolder text-uppercase">{formatIBAN(lang, item.qr_iban)}</label>
                      </div>
                      <div className="row mb-5">
                        <label className="text-muted fw-bold fs-8 d-flex align-items-center">
                          {lang.labels.reference}
                          <i className="ms-1 fs-10" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.informationAdditional}><FaExclamationCircle /></i>
                        </label>
                        <label className="fw-bolder text-uppercase d-flex align-items-center">
                          {item.reference.length > 0 ? item.reference : lang.labels.none}
                          { item.reference.length > 0 &&
                            <span className="badge badge-info fs-10 ms-2">{item.is_reference ? lang.labels.yes : lang.labels.no}</span>
                          }
                        </label>
                      </div>
                      <div className="border border-dashed border-gray-300 rounded-1 bg-lighten text-end w-100 px-2 py-1">
                        <p className="fw-bold p-0 m-0">{item.account_holder.full_name}</p>
                        <p className="text-secondary fs-9 p-0 m-0">{item.account_holder.address.street} {item.account_holder.address.number}</p>
                        <p className="text-secondary fs-9 p-0 m-0">{item.account_holder.address.additional.length > 0 && `${item.account_holder.address.additional}, `}{item.account_holder.address.administrative_area}</p>
                        <p className="text-secondary fs-9 p-0 m-0">{item.account_holder.address.city} - {item.account_holder.address.zip_code}</p>
                        <p className="text-secondary fs-9 p-0 m-0">{countryName(lang.code, item.account_holder.address.country)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex flex-wrap justify-content-center py-5">
                    <Link to={"/main/accounting/plan/view/" + item.accounting_account.id} className="btn btn-sm btn-light-primary mx-1 my-1">{lang.labels.accountStatement}</Link>
                    <Link to={`/main/treasury/bank-statement/list?id_treasury_account=${item.id}`} className="btn btn-sm btn-light-info mx-1 my-1">{lang.labels.bankStatements}</Link>
                    <Link to={`/main/treasury/bank-statement/transactions?id_treasury_account=${item.id}`} className="btn btn-sm btn-light-warning mx-1 my-1">{lang.labels.bankTransactions}</Link>
                    <button className="btn btn-sm btn-light-primary mx-1 my-1" type="button" onClick={() => executeShowModalTreasuryAccountUpdate(item)}>{lang.labels.edit}</button>
                    <button className="btn btn-sm btn-light-primary mx-1 my-1" type="button" onClick={() => submitTreasuryAccountDelete(item)}>{lang.labels.remove}</button>
                  </div>
                </div>
              </div>
            )}))}
          </div>
          :
          <EmptyState illustration={background} illustrationAlt="" title={lang.labels.noRecordsFound} description={lang.labels.startCreatingNewTreasuryAccountsForYourCompany} />
        }
        </>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalTreasuryUpdate companyForUser={companyForUser} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount} loadTreasuryAccountsForCompany={loadTreasuryAccountsForCompany}></AppModalTreasuryUpdate>
    </div>
  )
};

export default TreasuryListPage;
